import { useAppDispatch } from 'stores/type';
import { DeviceDomain } from 'models/Device';
import { setErrorMessageLocaleId } from 'stores/errorSlice';
import { ApplicationError } from 'models/Error';
import { OAuthDomain } from 'models/OAuth';
import { SAMLDomain } from 'models/SAML';
import { ROUTER_PATH } from 'views/constants';
import {
  clearEmail,
  logout as deviceLogout,
  fetched,
  reInstall,
  saveEmail,
  setDevice,
} from 'stores/deviceSlice';
import { useCallback } from 'react';

export const useDevice = (): {
  startInstallConfigurationProfile: () => Promise<void>;
  login: (
    email: string,
    password: string,
    invitationToken?: string
  ) => Promise<void>;
  loginFromOAuth: (onetimeToken: string) => Promise<void>;
  loginFromSAML: (onetimeToken: string) => Promise<void>;
  signUp: (
    transactionKey: string,
    password: string,
    name: string,
    locale: string,
    termsAccept: boolean,
    invitationToken?: string,
    oauthSessionToken?: string
  ) => Promise<void>;
  restore: () => Promise<void>;
  restoreFromWebclip: (onetimeToken: string) => Promise<string>;
  restoreFromMigration: (onetimeToken: string) => Promise<string>;
  logout: (deviceDomain: DeviceDomain) => Promise<void>;
  reInstallConfigurationProfile: () => Promise<void>;
  registerEmail: (deviceDomain: DeviceDomain, email: string) => Promise<void>;
  deleteEmail: (deviceDomain: DeviceDomain) => Promise<void>;
} => {
  const dispatch = useAppDispatch();

  const startInstallConfigurationProfile = useCallback(async () => {
    const result = await DeviceDomain.startInstallConfigurationProfile();

    if (result.isFailure()) {
      dispatch(setErrorMessageLocaleId(result.value.localeId()));
      return;
    }

    if (result.value) {
      dispatch(setDevice(result.value));
    }
  }, [dispatch]);

  const login = useCallback(
    async (
      email: string,
      password: string,
      invitationToken?: string
    ): Promise<void> => {
      const result = await DeviceDomain.login(email, password, invitationToken);
      if (result.isFailure()) {
        throw new ApplicationError(result.value.originalMessage);
      }

      if (result.value) {
        dispatch(setDevice(result.value));
      }
    },
    [dispatch]
  );

  const loginFromOAuth = useCallback(
    async (onetimeToken: string): Promise<void> => {
      const result = await OAuthDomain.login(onetimeToken);
      if (result.isFailure()) {
        throw new ApplicationError(result.value.localeId());
      }

      if (result.value) {
        dispatch(setDevice(result.value));
      }
    },
    [dispatch]
  );

  const loginFromSAML = useCallback(
    async (onetimeToken: string): Promise<void> => {
      const result = await SAMLDomain.login(onetimeToken);
      if (result.isFailure()) {
        // SMALログイン失敗エラーメッセージのlocaleは、/saml/sso/:ulid を踏んだ時にブラウザが付与するAccept-Languageヘッダーで決定しているため、レスポンスをそのまま表示する
        throw new ApplicationError(result.value.originalMessage);
      }

      if (result.value) {
        dispatch(setDevice(result.value));
      }
    },
    [dispatch]
  );

  const signUp = useCallback(
    async (
      transactionKey: string,
      password: string,
      name: string,
      locale: string,
      termsAccept: boolean,
      invitationToken?: string,
      oauthSessionToken?: string
    ): Promise<void> => {
      const result = await DeviceDomain.signUp(
        transactionKey,
        password,
        name,
        locale,
        termsAccept,
        invitationToken,
        oauthSessionToken
      );
      if (result.isFailure()) {
        throw new ApplicationError(
          result.value.originalMessage,
          result.value.errorDetails
        );
      }

      if (result.value) {
        dispatch(setDevice(result.value));
      }
    },
    [dispatch]
  );

  const restore = useCallback(async (): Promise<void> => {
    const device = await DeviceDomain.restore();
    if (device) {
      dispatch(setDevice(device));
    }

    dispatch(fetched());
  }, [dispatch]);

  const restoreFromWebclip = useCallback(
    async (onetimeToken: string): Promise<string> => {
      const result = await DeviceDomain.restoreFromWebclip(onetimeToken);

      if (result.isFailure()) {
        return ROUTER_PATH.login;
      }

      const [device, redirectPath] = result.value;

      if (device) {
        dispatch(setDevice(device));
      }
      return redirectPath;
    },
    [dispatch]
  );

  const restoreFromMigration = useCallback(
    async (onetimeToken: string): Promise<string> => {
      const result = await DeviceDomain.restoreFromMigration(onetimeToken);

      if (result.isFailure()) {
        return ROUTER_PATH.login;
      }

      const [device, redirectPath] = result.value;

      if (device) {
        dispatch(setDevice(device));
      }
      return redirectPath;
    },
    [dispatch]
  );

  const logout = useCallback(
    async (deviceDomain: DeviceDomain): Promise<void> => {
      const result = await deviceDomain.logout();

      if (result.isFailure()) {
        dispatch(setErrorMessageLocaleId(result.value.localeId()));
        throw new ApplicationError(result.value.originalMessage);
      }

      dispatch(deviceLogout());
    },
    [dispatch]
  );

  const reInstallConfigurationProfile = useCallback(async (): Promise<void> => {
    await DeviceDomain.reInstallConfigurationProfile();
    dispatch(reInstall());
  }, [dispatch]);

  const registerEmail = useCallback(
    async (deviceDomain: DeviceDomain, email: string): Promise<void> => {
      const result = await deviceDomain.registerEmail(email);

      if (result.isFailure()) {
        dispatch(setErrorMessageLocaleId(result.value.localeId()));
        throw new ApplicationError(result.value.originalMessage);
      }

      dispatch(saveEmail(email));
    },
    [dispatch]
  );

  const deleteEmail = useCallback(
    async (deviceDomain: DeviceDomain): Promise<void> => {
      const result = await deviceDomain.deleteEmail();

      if (result.isFailure()) {
        dispatch(setErrorMessageLocaleId(result.value.localeId()));
        throw new ApplicationError(result.value.originalMessage);
      }

      dispatch(clearEmail());
    },
    [dispatch]
  );

  return {
    startInstallConfigurationProfile,
    login,
    loginFromOAuth,
    loginFromSAML,
    signUp,
    restore,
    restoreFromWebclip,
    restoreFromMigration,
    logout,
    reInstallConfigurationProfile,
    registerEmail,
    deleteEmail,
  };
};
